import React, { useState } from "react"
import { IRecord } from "../../types/schema"
import { useGlobalState } from "../GlobalStateProvider"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { Fields } from "@cd2/forms"

type IWarehouse = IRecord<"warehouses", "name">

export function WarehouseSelect({
  value,
  onChange,
  disabled,
  label = "Warehouse",
  branchId,
  isClearable,
  filter,
  error,
  postFieldText,
  required,
}: {
  value?: string
  onChange(id: string, warehouse: IWarehouse): void
  disabled?: boolean
  label?: React.ReactNode
  branchId?: string
  isClearable?: boolean
  filter?: any
  error?: string
  postFieldText?: React.ReactNode
  required?: boolean
}) {
  const skeem = window.skeem
  const { currentUser } = useGlobalState()
  const [isLoading, setIsLoading] = useState(true)

  const [warehouses, setWarehouses] = useState<IWarehouse[]>([])

  function handleChange(id: string) {
    onChange(id, warehouses.find(w => w.id === id)!)
  }

  useAsyncEffect(async () => {
    try {
      const warehouses = await skeem.fetch2<IWarehouse[]>({
        model: {
          name: "warehouses",
          attributes: ["name", "disable_barscan"],
          filter: {
            and: [filter, { matches: { branch: branchId ?? currentUser.branch.id } }].filter(
              Boolean,
            ),
          },
          sort: { by: "name", direction: "asc" },
        },
      })
      setWarehouses(warehouses)
    } finally {
      setIsLoading(false)
    }
  }, [branchId, JSON.stringify(filter)])

  return (
    <Fields.Select2
      label={label}
      options={warehouses?.map(warehouse => ({ value: warehouse.id, label: warehouse.name }))}
      onChange={opt => handleChange(opt?.value)}
      value={value ? { value, label: warehouses.find(w => w.id === value)?.name } : undefined}
      disabled={disabled}
      isClearable={isClearable}
      isLoading={isLoading}
      error={error}
      postFieldText={postFieldText}
      showRequiredStar={required}
    />
  )
}

export function MultiWarehouseSelect({
  onChange,
  value,
  branchId,
}: {
  onChange(warehouse: IWarehouse[]): void
  value?: string[]
  branchId?: string
}) {
  const skeem = window.skeem
  const { currentUser } = useGlobalState()
  const [warehouses, setWarehouses] = useState<IWarehouse[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useAsyncEffect(async () => {
    try {
      const warehouses = await skeem.fetch2<IWarehouse[]>({
        model: {
          name: "warehouses",
          attributes: ["name", "disable_barscan"],
          filter: {
            and: [{ matches: { branch: branchId ?? currentUser.branch.id } }].filter(
              Boolean,
            ),
          },
          sort: { by: "name", direction: "asc" },
        },
      })
      setWarehouses(warehouses)
    } finally {
      setIsLoading(false)
    }
  }, [branchId])

  return (
    <Fields.Select2
      label="Warehouses"
      onChange={onChange}
      value={value ? warehouses.filter(w => value.includes(w.id)) : []}
      options={warehouses}
      isLoading={isLoading}
      getOptionLabel={opt => opt.name}
      getOptionValue={opt => opt.id}
      isClearable
      isMulti
    />
  )
}

